import { ScreenSpaceEventHandler, ScreenSpaceEventType, } from "cesium";
import { createPerFacePrimitive, updateFaceColors, } from "../../utilities/mesh-face-primitive";
import { buildAdjacencyMap, expandSelection, selectedFacesToGeometry, } from "../../utilities/mesh-face-selection";
// Strict on purpose: the reference is meant to be a plane, and imported surfaces
// with gentle (~25°) slopes leak across a loose tolerance.
const DEFAULT_ANGLE_TOLERANCE_DEG = 1;
/**
 * Manages face selection on a target mesh geometry.
 *
 * Uses a per-face Cesium Primitive where each face is a GeometryInstance
 * with id = { faceIndex }. Cesium's scene.pick identifies the face.
 *
 * All geometry is in WGS84 (ECEF) coordinates.
 */
export class FaceSelectionHandler {
    _viewer;
    _eventHandler = null;
    _geometry = null;
    _facePrimitiveResult = null;
    _adjacencyMap = null;
    _seedFaces = new Set();
    _excludedFaces = new Set();
    _selectedFaces = new Set();
    _hoveredFace = null;
    _angleToleranceDeg = DEFAULT_ANGLE_TOLERANCE_DEG;
    _expansionEnabled = true;
    _callback = null;
    _active = false;
    constructor(viewer) {
        this._viewer = viewer;
    }
    get isActive() {
        return this._active;
    }
    get selectedFaces() {
        return this._selectedFaces;
    }
    get seedFaces() {
        return this._seedFaces;
    }
    get excludedFaces() {
        return this._excludedFaces;
    }
    /**
     * Start face selection mode.
     * @param wgs84Geometry - geometry in WGS84 (ECEF) coordinates
     */
    start(wgs84Geometry, callback) {
        this.stop();
        this._geometry = wgs84Geometry;
        this._adjacencyMap = buildAdjacencyMap(wgs84Geometry);
        this._seedFaces = new Set();
        this._excludedFaces = new Set();
        this._selectedFaces = new Set();
        this._hoveredFace = null;
        this._callback = callback;
        this._active = true;
        this._facePrimitiveResult = createPerFacePrimitive(wgs84Geometry);
        this._viewer.scene.primitives.add(this._facePrimitiveResult.primitive);
        this._eventHandler = new ScreenSpaceEventHandler(this._viewer.scene.canvas);
        this._eventHandler.setInputAction(this._onMouseMove.bind(this), ScreenSpaceEventType.MOUSE_MOVE);
        this._eventHandler.setInputAction(this._onLeftClick.bind(this), ScreenSpaceEventType.LEFT_CLICK);
        this._eventHandler.setInputAction(this._onRightClick.bind(this), ScreenSpaceEventType.RIGHT_CLICK);
        this._callback?.onStart?.();
        this._viewer.scene.requestRender();
    }
    stop() {
        if (this._eventHandler) {
            this._eventHandler.destroy();
            this._eventHandler = null;
        }
        if (this._facePrimitiveResult) {
            this._viewer.scene.primitives.remove(this._facePrimitiveResult.primitive);
            this._facePrimitiveResult = null;
        }
        this._geometry = null;
        this._adjacencyMap = null;
        this._seedFaces = new Set();
        this._excludedFaces = new Set();
        this._selectedFaces = new Set();
        this._hoveredFace = null;
        this._active = false;
        this._callback = null;
    }
    setAngleTolerance(degrees) {
        this._angleToleranceDeg = degrees;
        this._recomputeSelection();
    }
    /**
     * Toggle BFS expansion from seeds. When disabled, only clicked faces are selected.
     * Persists across start/stop like the angle tolerance.
     */
    setExpansionEnabled(enabled) {
        if (this._expansionEnabled === enabled)
            return;
        this._expansionEnabled = enabled;
        this._recomputeSelection();
    }
    getSelectedGeometry() {
        if (!this._geometry || this._selectedFaces.size === 0)
            return null;
        return selectedFacesToGeometry(this._geometry, this._selectedFaces);
    }
    // --- Input handlers ---
    _onMouseMove(movement) {
        if (!this._facePrimitiveResult)
            return;
        const faceIndex = this._pickFaceIndex(movement.endPosition);
        if (faceIndex === this._hoveredFace)
            return;
        this._hoveredFace = faceIndex;
        this._updateColors();
        this._viewer.scene.requestRender();
    }
    _onLeftClick(click) {
        const faceIndex = this._pickFaceIndex(click.position);
        if (faceIndex === null)
            return;
        if (this._seedFaces.has(faceIndex)) {
            // Un-seed: remove from seeds
            this._seedFaces.delete(faceIndex);
        }
        else if (this._excludedFaces.has(faceIndex)) {
            // Un-exclude: remove from excluded
            this._excludedFaces.delete(faceIndex);
        }
        else if (this._selectedFaces.has(faceIndex)) {
            // Deselect: selected (via BFS) face → exclude it
            this._excludedFaces.add(faceIndex);
        }
        else {
            this._seedFaces.add(faceIndex);
            // Exclusions are BFS barriers, so a face excluded next to the new seed
            // would keep walling off the region behind it. Release the neighbours
            // so clicking beside an excluded face brings it back within tolerance.
            this._releaseAdjacentExclusions(faceIndex);
        }
        this._recomputeSelection();
        this._callback?.onProgress?.(this._selectedFaces, this._seedFaces, this._excludedFaces);
    }
    /** Right-click: toggle exclusion to lock/fix face state */
    _onRightClick(click) {
        const faceIndex = this._pickFaceIndex(click.position);
        if (faceIndex === null)
            return;
        if (this._excludedFaces.has(faceIndex)) {
            this._excludedFaces.delete(faceIndex);
        }
        else {
            this._seedFaces.delete(faceIndex);
            this._excludedFaces.add(faceIndex);
        }
        this._recomputeSelection();
        this._callback?.onProgress?.(this._selectedFaces, this._seedFaces, this._excludedFaces);
    }
    // --- Private helpers ---
    _pickFaceIndex(screenPosition) {
        const picks = this._viewer.scene.drillPick(screenPosition);
        for (const picked of picks) {
            const faceIndex = picked?.id?.faceIndex;
            if (typeof faceIndex === "number")
                return faceIndex;
        }
        return null;
    }
    /** Clears exclusions on the faces adjacent to the given face. */
    _releaseAdjacentExclusions(faceIndex) {
        const neighbors = this._adjacencyMap?.get(faceIndex);
        if (!neighbors)
            return;
        for (const neighbor of neighbors) {
            this._excludedFaces.delete(neighbor);
        }
    }
    _recomputeSelection() {
        if (!this._geometry || !this._adjacencyMap)
            return;
        const angleToleranceRad = (this._angleToleranceDeg * Math.PI) / 180;
        // expandSelection already skips excluded faces, no post-filter needed.
        // Seeds and exclusions are disjoint (right-click moves a seed to excluded).
        this._selectedFaces = this._expansionEnabled
            ? expandSelection(this._geometry, this._adjacencyMap, this._seedFaces, this._excludedFaces, angleToleranceRad)
            : new Set(this._seedFaces);
        this._updateColors();
        this._viewer.scene.requestRender();
    }
    _updateColors() {
        if (!this._facePrimitiveResult)
            return;
        updateFaceColors(this._facePrimitiveResult.primitive, this._facePrimitiveResult.faceIds, this._selectedFaces, this._seedFaces, this._excludedFaces, this._hoveredFace);
    }
}
//# sourceMappingURL=face-selection-handler.js.map